import { useState } from "react";
import { formatDateTime } from "../../api/dates";
import {
  EVIDENCE_LABEL,
  useGrantOverride,
  useReadiness,
  useRevokeOverride,
  useSetReadinessItem,
  useSetWorkOrderType,
  type OverrideView,
  type Readiness,
  type ReadinessLine,
} from "../../api/readiness";
import { StatusChip, type Tone } from "../../components/ui";

const STATE_LABEL: Record<ReadinessLine["state"], string> = { open: "Open", confirmed: "Confirmed", not_needed: "Not needed" };
const STATE_TONE: Record<ReadinessLine["state"], Tone> = { open: "warn", confirmed: "ok", not_needed: "info" };
const BASIS_LABEL: Record<Readiness["journey"]["basis"], string> = {
  default: "PM42 by default",
  work_order: "from the work order",
  inspection: "from its first visit",
};

/** A job's readiness, office only: which journey it follows and why, what the
 * scheduling gate says, the nine lines (three read from records, six the
 * office confirms), and -- for an administrator -- the emergency override. */
export default function ReadinessPanel({ siteId }: { siteId: number }) {
  const view = useReadiness(siteId);
  if (view.isPending) {
    return <p className="empty-state">Loading readiness…</p>;
  }
  if (view.isError) {
    return <p className="notice bad">Readiness could not be loaded: {view.error.message}</p>;
  }
  const panel = view.data;
  return (
    <div className="readiness-panel">
      <JourneySection panel={panel} />
      <GateSection panel={panel} />
      <section>
        <h4>Readiness</h4>
        <table className="data-table">
          <tbody>
            {panel.items.map((item) => (
              <ItemRow key={item.key} siteId={siteId} item={item} />
            ))}
          </tbody>
        </table>
      </section>
      <OverrideSection panel={panel} />
    </div>
  );
}

function JourneySection({ panel }: { panel: Readiness }) {
  const save = useSetWorkOrderType(panel.site_id);
  const [value, setValue] = useState(panel.work_order_type ?? "");
  return (
    <section>
      <h4>Journey</h4>
      <p>
        {panel.journey.kind === "pm42" ? "PM42 service" : "PM41"} <StatusChip tone="quiet">{BASIS_LABEL[panel.journey.basis]}</StatusChip>
      </p>
      <label className="field">
        Work order type
        <select value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Not stated</option>
          <option value="PM41">PM41 (repair, installation, assessment)</option>
          <option value="PM42">PM42 (routine service)</option>
        </select>
      </label>
      <button
        type="button"
        className="btn"
        disabled={save.isPending || value === (panel.work_order_type ?? "")}
        onClick={() => save.mutate({ work_order_type: value === "" ? null : value, note: null })}
      >
        Save the type
      </button>
      {save.isError ? <p className="notice bad">{save.error.message}</p> : null}
    </section>
  );
}

function GateSection({ panel }: { panel: Readiness }) {
  const { gate } = panel;
  return (
    <section>
      <h4>Scheduling gate</h4>
      {!gate.enabled ? <p className="muted-note">The gate is switched off: planning does not hold this job.</p> : null}
      {!gate.applies ? (
        <p>PM41 jobs are not gated.</p>
      ) : gate.passes ? (
        <StatusChip tone="ok">Ready to schedule</StatusChip>
      ) : (
        <ul className="readiness-reasons">
          {gate.reasons.map((reason) => (
            <li key={reason}>
              <StatusChip tone="warn">{reason}</StatusChip>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ItemRow({ siteId, item }: { siteId: number; item: ReadinessLine }) {
  const save = useSetReadinessItem(siteId);
  const [excusing, setExcusing] = useState(false);
  const [note, setNote] = useState("");
  const office = item.source === "office";
  return (
    <tr>
      <th scope="row">{item.label}</th>
      <td>
        <StatusChip tone={STATE_TONE[item.state]}>{STATE_LABEL[item.state]}</StatusChip>
        {item.reference ? <span className="sub mono">{item.reference}</span> : null}
      </td>
      <td>
        {item.by ? `${item.by}, ${formatDateTime(item.at)}` : "—"}
        {item.note ? <span className="sub">{item.note}</span> : null}
      </td>
      <td>
        {office && item.state === "open" ? (
          <>
            <button type="button" className="row-button" disabled={save.isPending} onClick={() => save.mutate({ item: item.key, state: "confirmed", note: null })}>
              Confirm
            </button>
            <button type="button" className="row-button" onClick={() => setExcusing((open) => !open)}>
              Not needed…
            </button>
          </>
        ) : null}
        {office && item.state !== "open" ? (
          <button type="button" className="row-button" disabled={save.isPending} onClick={() => save.mutate({ item: item.key, state: "open", note: null })}>
            Re-open
          </button>
        ) : null}
        {excusing ? (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ item: item.key, state: "not_needed", note: note.trim() }, { onSuccess: () => setExcusing(false) });
            }}
          >
            <label className="field">
              Why not needed
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            </label>
            <button type="submit" className="btn" disabled={note.trim() === "" || save.isPending}>
              Save
            </button>
          </form>
        ) : null}
        {save.isError ? <p className="notice bad">{save.error.message}</p> : null}
      </td>
    </tr>
  );
}

function OverrideSection({ panel }: { panel: Readiness }) {
  const current: OverrideView | null = panel.override;
  const active = current !== null && current.active;
  return (
    <section>
      <h4>Emergency override</h4>
      {current ? <OverrideFacts override={current} /> : <p className="muted-note">None.</p>}
      {panel.can_override && active ? <RevokeForm siteId={panel.site_id} /> : null}
      {panel.can_override && !active && panel.gate.applies && !panel.gate.passes ? <GrantForm siteId={panel.site_id} /> : null}
    </section>
  );
}

function OverrideFacts({ override }: { override: OverrideView }) {
  return (
    <dl className="insp-facts">
      <div className="fact-pair">
        <dt>Granted</dt>
        <dd>
          {formatDateTime(override.granted_at)} by {override.granted_by ?? "—"}
        </dd>
      </div>
      <div className="fact-pair">
        <dt>Reason</dt>
        <dd>{override.reason}</dd>
      </div>
      <div className="fact-pair">
        <dt>Evidence</dt>
        <dd>
          <a href={override.document_url} target="_blank" rel="noreferrer">
            {EVIDENCE_LABEL[override.evidence_kind]}
          </a>
        </dd>
      </div>
      {override.active ? null : (
        <div className="fact-pair">
          <dt>Revoked</dt>
          <dd>
            {formatDateTime(override.revoked_at)} by {override.revoked_by ?? "—"}: {override.revoke_reason}
          </dd>
        </div>
      )}
    </dl>
  );
}

function GrantForm({ siteId }: { siteId: number }) {
  const grant = useGrantOverride(siteId);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState<OverrideView["evidence_kind"] | "">("");
  const [file, setFile] = useState<File | null>(null);
  if (!open) {
    return (
      <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)}>
        Grant emergency override…
      </button>
    );
  }
  const ready = reason.trim() !== "" && kind !== "" && file !== null;
  return (
    <form
      className="override-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (kind !== "" && file !== null && reason.trim() !== "") {
          grant.mutate({ reason: reason.trim(), evidence_kind: kind, file }, { onSuccess: () => setOpen(false) });
        }
      }}
    >
      <label className="field">
        Reason
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
      </label>
      <fieldset>
        <legend>What authorises it</legend>
        <label className="check">
          <input type="radio" name="evidence" checked={kind === "email"} onChange={() => setKind("email")} /> E-mail
        </label>
        <label className="check">
          <input type="radio" name="evidence" checked={kind === "emergency_qcp"} onChange={() => setKind("emergency_qcp")} /> Emergency QCP
        </label>
      </fieldset>
      <label className="field">
        The e-mail or emergency QCP
        <input type="file" accept="application/pdf,.pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </label>
      {/* Outside the label, so the input's accessible name stays exactly "The e-mail or emergency QCP". */}
      <p className="muted">Save the e-mail as a PDF (Print, then Save as PDF), or attach the emergency QCP.</p>
      <button type="submit" className="btn btn-primary" disabled={!ready || grant.isPending}>
        Grant the override
      </button>
      <button type="button" className="btn" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {grant.isError ? <p className="notice bad">{grant.error.message}</p> : null}
    </form>
  );
}

function RevokeForm({ siteId }: { siteId: number }) {
  const revoke = useRevokeOverride(siteId);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        Revoke…
      </button>
    );
  }
  return (
    <form
      className="override-form"
      onSubmit={(e) => {
        e.preventDefault();
        revoke.mutate(reason.trim(), { onSuccess: () => setOpen(false) });
      }}
    >
      <label className="field">
        Why it is being revoked
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
      </label>
      <button type="submit" className="btn btn-primary" disabled={reason.trim() === "" || revoke.isPending}>
        Revoke the override
      </button>
      {revoke.isError ? <p className="notice bad">{revoke.error.message}</p> : null}
    </form>
  );
}
